import { jsPDF } from "jspdf";
import { capitalize, lowerCase } from "lodash";
import { ArrowLeft, FileDown } from "lucide-react";
import { Fragment, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Preloader from "@krea/customer-features/shared-components/preloader";
import { Button } from "@krea/shared/design-system/Button";

import { useGetLenderApplicationDetailQuery } from "@/features/lender-applications/hooks/data/use-get-lender-application-detail.query";
import type { IAccountingDataListRows } from "@/types/accountingData";

import { useGetAccountingData } from "../../store/accounting-data";
import AccountingDataList from "./list";

const IncomeStatement: IAccountingDataListRows[] = [
  {
    title: "",
    rows: [
      { field: "revenue", value: "Revenues", isBold: false },
      { field: "personnelCosts", value: "Personal costs", isBold: false },
      {
        field: "operatingCosts",
        value: "Other Operating Costs",
        isBold: false,
      },
      { field: "ebitda", value: "EBITDA", isBold: true },
    ],
  },
  {
    title: "",
    rows: [
      {
        field: "depreciationAndWritedowns",
        value: "Depreciation and amortisation",
        isBold: false,
      },
      { field: "ebit", value: "EBIT", isBold: true },
    ],
  },
  {
    title: "",
    rows: [
      { field: "financialIncomes", value: "Financial incomes", isBold: false },
      { field: "financialExpenses", value: "Financial cost", isBold: false },
      {
        field: "extraordinaryIncomeAndExpenses",
        value: "Extraordinary income and expenses",
        isBold: false,
      },
      { field: "currentProfit", value: "Profit before Tax", isBold: true },
    ],
  },
  {
    title: "",
    rows: [
      { field: "appropriations", value: "Appropriations", isBold: false },
      { field: "tax", value: "Tax", isBold: false },
      { field: "result", value: "Net profit", isBold: true },
    ],
  },
];

const BalanceSheet: IAccountingDataListRows[] = [
  {
    title: "Assets",
    rows: [
      {
        field: "cashAndCashEquivalent",
        value: "Cash & cash equivalents",
        isBold: false,
      },
      {
        field: "accountsReceivable",
        value: "Accounts receivables",
        isBold: false,
      },
      {
        field: "otherCurrentReceivables",
        value: "Other current assets",
        isBold: false,
      },
      {
        field: "warehouseAndWorkInProgress",
        value: "Inventories",
        isBold: false,
      },
      {
        field: "totalLiquidAssets",
        value: "Total current assets",
        isBold: true,
      },
    ],
  },
  {
    title: "",
    rows: [
      {
        field: "buildingsAndFreeholdLand",
        value: "Land & building",
        isBold: false,
      },
      {
        field: "equipmentsAndTools",
        value: "Equipment & machines",
        isBold: false,
      },
      {
        field: "financialFixAssets",
        value: "Financial assets",
        isBold: false,
      },
      {
        field: "otherTangibleAssets",
        value: "Other tangible assets",
        isBold: false,
      },
      { field: "intangibleAssets", value: "Intangible assets", isBold: false },
      { field: "totalFixAssets", value: "Total fixed assets", isBold: true },
    ],
  },
  {
    title: "",
    rows: [{ field: "totalAssets", value: "Total assets", isBold: true }],
  },
  {
    title: "Liabilities, Equity & Depreciation",
    rows: [
      { field: "restrictedEquity", value: "Restricted equity", isBold: false },
      {
        field: "unrestrictedEquity",
        value: "Unrestricted equity",
        isBold: false,
      },
      { field: "equity", value: "Total Equity", isBold: true },
    ],
  },
  {
    title: "",
    rows: [
      { field: "accountPayable", value: "Accounts payables", isBold: false },
      {
        field: "shortTermLiabilitiesToCreditInstitutions",
        value: "Short-term liabilities to credit institutions",
        isBold: false,
      },
      {
        field: "otherCurrentLiabilities",
        value: "Other short-term liabilities",
        isBold: false,
      },
      {
        field: "currentLiabilities",
        value: "Total short-term liabilities",
        isBold: true,
      },
    ],
  },
  {
    title: "",
    rows: [
      {
        field: "longTermLiabilitiesToCreditInstitutions",
        value: "Long-term liabilities to credit institutions",
        isBold: false,
      },
      {
        field: "otherLongtermLiabilities",
        value: "Other long-term liabilities",
        isBold: false,
      },
      {
        field: "totalLongtermLiabilities",
        value: "Total long-term liabilities",
        isBold: true,
      },
    ],
  },
  {
    title: "",
    rows: [
      {
        field: "sumOfLiabilitiesAndEquity",
        value: "Total liabilities and equity",
        isBold: true,
      },
    ],
  },
];

const FinancialRatios: IAccountingDataListRows[] = [
  {
    title: "",
    rows: [
      { field: "operatingMargin", value: "EBIT margin", isBold: false },
      { field: "quickRatio", value: "Quick Ratio", isBold: false },
      { field: "solvency", value: "Solidity", isBold: false },
      {
        field: "interestCoverageRatio",
        value: "Interest coverage ratio",
        isBold: false,
      },
      { field: "profitRatio", value: "Net profit margin", isBold: false },
      // { field: '', value: 'Nettoskuld / EBITDA', isBold: false },
      // { field: 'working_capital', value: 'Working capital in % of sales', isBold: false }
    ],
  },
];

const AccountingData = () => {
  const { lenderCompanyId, id = "" } = useParams<{
    lenderCompanyId: string;
    id: string;
  }>();

  const navigate = useNavigate();
  const refContent = useRef<HTMLDivElement>(null);

  const { data: lenderApplication, isPending: isLoadingLenderDetails } =
    useGetLenderApplicationDetailQuery(Number(lenderCompanyId), Number(id));

  const { data: accountingData, isPending: isLoadingAccountingData } =
    useGetAccountingData(Number(lenderApplication?.loanApplication.id), {
      enabled: !!lenderApplication?.loanApplication.id,
    });

  const pdfOrg = capitalize(
    lowerCase(accountingData?.customerCompany?.name ?? ""),
  );
  const pdfOrgNum = capitalize(
    lowerCase(accountingData?.customerCompany?.organisationNumber ?? ""),
  );

  const isLoading = useMemo(
    () => isLoadingAccountingData || isLoadingLenderDetails,
    [isLoadingAccountingData, isLoadingLenderDetails],
  );

  const createPdf = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const htmlOptions = {
      callback: (doc: { save: (arg0: string) => void }) => {
        doc.save(`Accounting-Data_${pdfOrg}_${pdfOrgNum}.pdf`);
      },
      autoPaging: "text",
      x: 0,
      y: 28,
      margin: [0, 5, 10, 5],
      width: 287,
      windowWidth: 1384, // px
    };

    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0, 1);
    doc.text(`Accounting data - ${pdfOrg} (${pdfOrgNum})`, 5, 15);
    doc.setFontSize(12);

    if (refContent.current) {
      doc.html(refContent.current, { ...htmlOptions, autoPaging: true });
    } else {
      // Handle the case when refContent.current is null
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Preloader className="text-primary" size="lg" />;
      </div>
    );
  }

  return (
    <Fragment>
      <div className="mb-6 flex flex-col flex-wrap items-start border-b pb-3 md:flex-row md:items-center md:justify-between">
        <Button
          variant="secondary"
          className="flex items-center px-4"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="size-4" />
          <span>Go back</span>
        </Button>
      </div>

      <div className="-mx-3 mb-7 flex flex-wrap">
        <div className="w-full px-3 pt-5">
          <div className="-mx-3 mb-6 flex flex-wrap items-center justify-end">
            <div className="w-full px-3">
              {accountingData?.customerCompany && (
                <h3 className="mx-0 mb-4">{`Accounting data - ${capitalize(
                  lowerCase(accountingData.customerCompany.name),
                )} (${accountingData.customerCompany.organisationNumber})`}</h3>
              )}
            </div>

            <div className="mt-4 w-full shrink-0 px-3 sm:m-0 sm:w-auto">
              <div className="-mx-3 mb-5 flex flex-wrap items-center justify-between">
                <div className="mt-4 w-full shrink-0 px-3 sm:mt-0 sm:w-auto">
                  <Button
                    variant="secondary"
                    className="flex items-center justify-center"
                    onClick={createPdf}
                  >
                    <FileDown className="size-4" />
                    <div>Save as PDF file</div>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div ref={refContent}>
            <AccountingDataList
              header="Profit Loss"
              className="mb-8"
              data={accountingData?.profitLossEntries ?? []}
              rows={IncomeStatement}
              currency={lenderApplication?.loanApplication.currency}
            />

            <AccountingDataList
              header="Balance Sheet"
              className="mb-8"
              data={accountingData?.balanceSheetEntries ?? []}
              rows={BalanceSheet}
              currency={lenderApplication?.loanApplication.currency}
            />

            <AccountingDataList
              header="Financial Ratio"
              data={accountingData?.financialRatioEntries ?? []}
              rows={FinancialRatios}
              currency={lenderApplication?.loanApplication.currency}
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AccountingData;
